import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  clearActingTenant as clearActingTenantRequest,
  fetchFortnoxConnectionStatus,
  getLoggedOutAuthState,
  loginUser,
  logoutUser,
  restoreAuthSession,
  setActingTenant as setActingTenantRequest,
  syncStoredCompany,
} from '../auth/session';

export default function useAuthSession() {
  const [authState, setAuthState] = useState(getLoggedOutAuthState);
  const [authResolved, setAuthResolved] = useState(false);
  const [loadingConnection, setLoadingConnection] = useState(true);
  const currentRole = authState.currentUser?.role;
  const actingTenantId = authState.actingTenant?.id ?? null;

  const tenantContextRequired = useMemo(
    () => currentRole === 'SUPER_ADMIN' && actingTenantId == null,
    [currentRole, actingTenantId]
  );

  const applyLoggedOutState = useCallback(() => {
    setAuthState(getLoggedOutAuthState());
  }, []);

  const applyAuthState = useCallback((nextState) => {
    setAuthState(nextState);
  }, []);

  const setFortnoxConnected = useCallback((nextValue) => {
    setAuthState((currentState) => {
      const resolvedValue =
        typeof nextValue === 'function'
          ? Boolean(nextValue(currentState.fortnoxConnected))
          : Boolean(nextValue);

      if (currentState.fortnoxConnected === resolvedValue) {
        return currentState;
      }

      return {
        ...currentState,
        fortnoxConnected: resolvedValue,
      };
    });
  }, []);

  const restoreSession = useCallback(async () => {
    setLoadingConnection(true);

    try {
      const session = await restoreAuthSession();
      applyAuthState(session);
      return session;
    } catch (error) {
      applyLoggedOutState();
      throw error;
    } finally {
      setAuthResolved(true);
      setLoadingConnection(false);
    }
  }, [applyAuthState, applyLoggedOutState]);

  const login = useCallback(
    async ({ username, password, company }) => {
      setLoadingConnection(true);

      try {
        const session = await loginUser({ username, password, company });
        syncStoredCompany(company);
        applyAuthState(session);
        setAuthResolved(true);
        return session;
      } finally {
        setLoadingConnection(false);
      }
    },
    [applyAuthState]
  );

  const refreshConnection = useCallback(async () => {
    if (!authState.loggedIn) {
      setFortnoxConnected(false);
      setLoadingConnection(false);
      return { authorized: false, connected: false, tenantContextRequired: false };
    }

    if (currentRole === 'SUPER_ADMIN' && actingTenantId == null) {
      setFortnoxConnected(false);
      setLoadingConnection(false);
      return { authorized: true, connected: false, tenantContextRequired: true };
    }

    setLoadingConnection(true);

    try {
      const connectionState = await fetchFortnoxConnectionStatus();

      if (!connectionState.authorized) {
        applyLoggedOutState();
        return { ...connectionState, tenantContextRequired: false };
      }

      if (connectionState.forbidden) {
        if (currentRole === 'SUPER_ADMIN' && actingTenantId == null) {
          setFortnoxConnected(false);
          return { authorized: true, connected: false, tenantContextRequired: true };
        }

        const error = new Error('Failed to check connection status: Forbidden.');
        error.status = 403;
        throw error;
      }

      setFortnoxConnected(connectionState.connected);
      return { ...connectionState, tenantContextRequired: false };
    } catch (error) {
      setFortnoxConnected(false);
      throw error;
    } finally {
      setLoadingConnection(false);
    }
  }, [actingTenantId, applyLoggedOutState, authState.loggedIn, currentRole, setFortnoxConnected]);

  const setActingTenant = useCallback(
    async (tenantId) => {
      const session = await setActingTenantRequest(tenantId);
      applyAuthState(session);
      setAuthResolved(true);
      return session;
    },
    [applyAuthState]
  );

  const clearActingTenant = useCallback(async () => {
    try {
      const session = await clearActingTenantRequest();
      applyAuthState(session);
      setAuthResolved(true);
      return session;
    } catch (error) {
      if (error.status === 401) {
        applyLoggedOutState();
      }
      throw error;
    }
  }, [applyAuthState, applyLoggedOutState]);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      applyLoggedOutState();
      setAuthResolved(true);
      setLoadingConnection(false);
    }
  }, [applyLoggedOutState]);

  useEffect(() => {
    restoreSession().catch((error) => {
      console.error('Error restoring auth session:', error);
    });
  }, [restoreSession]);

  return {
    actingTenant: authState.actingTenant,
    authResolved,
    clearActingTenant,
    connected: authState.fortnoxConnected,
    crmEnabled: authState.crmEnabled,
    currentUser: authState.currentUser,
    fortnoxConnected: authState.fortnoxConnected,
    loadingConnection,
    loggedIn: authState.loggedIn,
    login,
    logout,
    refreshConnection,
    restoreSession,
    setActingTenant,
    setConnected: setFortnoxConnected,
    tenantContextRequired,
  };
}
